import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ContactService } from "@main-services/contact.service";
import { Contact, ContactField, Cryptocoin } from "@models";
import { TranslationPipe } from "@modules/core/pipes/translation-pipe/translation.pipe";
import { TranslationService } from "@core-services/translation.service";
import { ContactFieldService } from "@modules/main/components/step-summary/sending-modal/sending-recipient/services/contact-field.service";

@Component({
	selector: "select-contact",
	templateUrl: "./select-contact.component.html",
	providers: [ContactFieldService],
	standalone: false
})
export class SelectContactComponent implements OnInit {
	@Input() asset: Cryptocoin;
	@Input() currentSearchString: string = "";
	@Output() onSelectContact: EventEmitter<ContactField> = new EventEmitter<ContactField>();
	@Output() onSelectNewAddress: EventEmitter<void> = new EventEmitter<void>();
	@Output() onMultiplePossibleRecipients: EventEmitter<string> = new EventEmitter<string>();
	public contacts: Contact[];
	private translationPipe: TranslationPipe = null;
	constructor(
		private contactService: ContactService,
		private translationService: TranslationService,
		private contactFieldService: ContactFieldService
	) {
		this.translationPipe = new TranslationPipe(this.translationService);
	}

	public ngOnInit(): void {
		this.contacts = this.contactService.contacts;
	}

	public getFilteredContacts(): Contact[] {
		return this.contactService
			.sorted(this.contacts.filter((contact) => this.getPossibleFieldsOfContact(contact).length > 0))
			.slice(0, 10);
	}

	public filteredContacts(): ContactField[] {
		return (
			this.currentSearchString.trim() === ""
				? []
				: this.contacts
						.filter(
							(contact) =>
								contact.getName().toLocaleLowerCase().indexOf(this.currentSearchString.toLowerCase()) >
								-1
						) // Find all matching contacts
						.reduce((contacts, contact) => contacts.concat(contact.getFields()), []) // Get their fields
						.concat(
							this.contacts // Find all matching fields
								.reduce((contacts, contact) => contacts.concat(contact.getFields()), [])
								.filter(
									(field: ContactField) =>
										field
											.getName()
											.toLocaleLowerCase()
											.indexOf(this.currentSearchString.toLowerCase()) > -1 ||
										field
											.getValue()
											.toLocaleLowerCase()
											.indexOf(this.currentSearchString.toLowerCase()) > -1
								)
						)
		)
			.sort((cf: ContactField, cf2: ContactField) => cf2.lastUsed() - cf.lastUsed()) // Sort by last used desc
			.filter((el, i, a) => i === a.indexOf(el)) // Filter Duplicates
			.filter(
				(field) => field.getSymbol() === "EMAIL" || field.getSymbol() === this.asset.getSymbol().toUpperCase()
			); // Only show possible addresses for the wallet the user chose
	}

	public showContactsSection(): boolean {
		return this.contacts.length > 0 && this.currentSearchString?.length === 0;
	}

	public getPossibleFieldsOfContact(c: Contact): ContactField[] {
		return c
			.getFields(false, true)
			.filter(
				(field) => field.getSymbol() === "EMAIL" || field.getSymbol() === this.asset.getSymbol().toUpperCase()
			);
	}

	public showContactInfo(field: ContactField): boolean {
		return !!field.getName() || this.isCurrentContactField(field);
	}

	public selectContact(c: Contact): void {
		const possibleFields: ContactField[] = this.getPossibleFieldsOfContact(c);
		if (possibleFields.length > 1) {
			this.currentSearchString = c.getName();
			return;
		}
		this.onSelectContact.emit(possibleFields[0]);
	}

	public showNewAddressField(): boolean {
		return this.currentSearchString.length > 0;
	}

	public showFilteredContacts(): boolean {
		return this.currentSearchString.length > 0;
	}

	public selectNewAddress(): void {
		this.onSelectNewAddress.emit();
	}

	public getFieldStringByUseCase(field: ContactField, useCase: string = ""): string {
		return (
			this.getFieldName(field, useCase)[0] +
			this.getFieldName(field, useCase)[1] +
			this.getFieldName(field, useCase)[2]
		);
	}

	public matchingContact(field: ContactField): Contact[] {
		return this.contactFieldService.matchingContact(field);
	}

	public getFieldName(field: ContactField, useCase: string = ""): string[] {
		let fieldName: string = "";
		if (useCase === "info") {
			fieldName = this.matchingContact(field)[0].getName();
		} else if (useCase === "value") {
			fieldName = field.getValue();
		} else {
			fieldName = field.getName()
				? field.getName()
				: this.translationPipe.transform(this.getRecentContactDescription(field), [this.asset.getSymbol()]);
		}

		const lowerCase: string = fieldName.toLowerCase();

		const returnName: string[] = ["", "", ""];
		if (lowerCase.includes(this.currentSearchString.toLowerCase()) && this.currentSearchString.length > 0) {
			if (lowerCase.indexOf(this.currentSearchString.toLowerCase()) === 0) {
				returnName[0] = "<strong>" + fieldName.substring(0, this.currentSearchString.length) + "</strong>";
				returnName[1] = fieldName.substring(this.currentSearchString.length, fieldName.length);
			} else if (
				lowerCase.indexOf(this.currentSearchString.toLowerCase()) + this.currentSearchString.length ===
				fieldName.length
			) {
				returnName[0] = fieldName.substring(0, lowerCase.indexOf(this.currentSearchString.toLowerCase()));
				returnName[1] =
					"<strong>" +
					fieldName.substring(lowerCase.indexOf(this.currentSearchString.toLowerCase()), fieldName.length) +
					"</strong>";
			} else {
				returnName[0] = fieldName.substring(0, lowerCase.indexOf(this.currentSearchString.toLowerCase()));
				returnName[1] =
					"<strong>" +
					fieldName.substring(
						lowerCase.indexOf(this.currentSearchString.toLowerCase()),
						lowerCase.indexOf(this.currentSearchString.toLowerCase()) + this.currentSearchString.length
					) +
					"</strong>";
				returnName[2] = fieldName.substring(
					lowerCase.indexOf(this.currentSearchString.toLowerCase()) + this.currentSearchString.length,
					fieldName.length
				);
			}
		} else {
			returnName[1] = fieldName;
		}
		return returnName;
	}

	public getRecentContactDescription(field: ContactField): string {
		return this.contactFieldService.getRecentContactDescription(field);
	}

	public isCurrentContactField(field: ContactField): boolean {
		return this.matchingContact(field).length > 0;
	}

	public selectFilteredContact(field: ContactField): void {
		this.onSelectContact.emit(field);
	}
}
